import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Input, Modal, Form, Select, DatePicker, Popconfirm, message, Descriptions, Divider } from 'antd';
import { ReloadOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { customerApi } from '../../services/api';
import dayjs from 'dayjs';

const Customers = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [keyword, setKeyword] = useState('');

  // 详情弹框
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [customerDetail, setCustomerDetail] = useState(null);

  // 编辑弹框
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editForm] = Form.useForm();
  const [editingId, setEditingId] = useState(null);

  useEffect(() => { fetchData(1); }, []);

  const fetchData = async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: pagination.pageSize };
      if (keyword.trim()) params.keyword = keyword.trim();
      const res = await customerApi.list(params);
      if (res.success) {
        setData(res.data.list);
        setPagination({ ...pagination, current: page, total: res.data.pagination.total });
      }
    } catch (error) {
      message.error('获取客户列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetail = async (record) => {
    setDetailLoading(true);
    setDetailModalVisible(true);
    try {
      const res = await customerApi.detail(record.id);
      if (res.success) {
        setCustomerDetail(res.data);
      }
    } catch (error) {
      message.error('获取客户详情失败');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleEdit = (record) => {
    setEditingId(record.id);
    editForm.setFieldsValue({
      last_name: record.last_name,
      first_name: record.first_name,
      gender: record.gender,
      birthday: record.birthday ? dayjs(record.birthday) : null,
      phone: record.phone,
      email: record.email
    });
    setEditModalVisible(true);
  };

  const handleEditSubmit = async () => {
    try {
      const values = await editForm.validateFields();
      setEditLoading(true);
      await customerApi.update(editingId, {
        ...values,
        birthday: values.birthday ? values.birthday.format('YYYY-MM-DD') : null
      });
      message.success('客户信息更新成功');
      setEditModalVisible(false);
      fetchData(pagination.current);
    } catch (error) {
      message.error(error.response?.data?.message || '更新失败');
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await customerApi.delete(id);
      message.success('客户删除成功');
      fetchData(pagination.current);
    } catch (error) {
      message.error(error.response?.data?.message || '删除失败');
    }
  };

  const columns = [
    { title: '姓名', render: (_, r) => `${r.last_name}${r.first_name}`, width: 120 },
    {
      title: '性别', dataIndex: 'gender', width: 60,
      render: (v) => v === 1 ? '男' : v === 2 ? '女' : '-'
    },
    { title: '手机号', dataIndex: 'phone', width: 130 },
    { title: '邮箱', dataIndex: 'email', ellipsis: true },
    {
      title: '购买次数', dataIndex: 'purchase_count', width: 90,
      render: (v) => v || 0
    },
    {
      title: '注册时间', dataIndex: 'created_at', width: 160,
      render: (v) => v ? new Date(v).toLocaleString('zh-CN') : '-'
    },
    {
      title: '操作', width: 180, fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => handleViewDetail(record)}>详情</Button>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>编辑</Button>
          <Popconfirm title="确定删除该客户？" onConfirm={() => handleDelete(record.id)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => fetchData(pagination.current)}>刷新</Button>
          <Input.Search placeholder="搜索姓名或手机号" value={keyword}
            onChange={(e) => setKeyword(e.target.value)} onSearch={() => fetchData(1)} style={{ width: 220 }} />
        </Space>
      </div>

      <Table columns={columns} dataSource={data} loading={loading} rowKey="id" scroll={{ x: 900 }}
        pagination={{ ...pagination, onChange: (page) => fetchData(page) }} />

      {/* 客户详情弹框 */}
      <Modal title="客户详情" open={detailModalVisible} onCancel={() => setDetailModalVisible(false)} footer={null} width={700} loading={detailLoading}>
        {customerDetail && (
          <>
            <Descriptions column={2} size="small" bordered>
              <Descriptions.Item label="姓名">{customerDetail.customer.last_name}{customerDetail.customer.first_name}</Descriptions.Item>
              <Descriptions.Item label="性别">{customerDetail.customer.gender === 1 ? '男' : customerDetail.customer.gender === 2 ? '女' : '-'}</Descriptions.Item>
              <Descriptions.Item label="手机号">{customerDetail.customer.phone}</Descriptions.Item>
              <Descriptions.Item label="邮箱">{customerDetail.customer.email || '-'}</Descriptions.Item>
              <Descriptions.Item label="出生日期">{customerDetail.customer.birthday || '-'}</Descriptions.Item>
              <Descriptions.Item label="注册时间">{customerDetail.customer.created_at ? new Date(customerDetail.customer.created_at).toLocaleString('zh-CN') : '-'}</Descriptions.Item>
            </Descriptions>

            <Divider orientation="left" style={{ marginTop: 20 }}>购买记录</Divider>
            <Table
              dataSource={customerDetail.purchases || []}
              rowKey="id"
              size="small"
              pagination={false}
              columns={[
                { title: '货品编码', dataIndex: 'product_code', width: 120 },
                { title: '货品名称', dataIndex: 'product_name' },
                { title: '品类', dataIndex: 'category', width: 80 },
                { title: '购买门店', dataIndex: 'store_name', width: 100 },
                { title: '销售价格', dataIndex: 'sale_price', width: 100, render: (v) => v ? `¥${v}` : '-' },
                { title: '购买日期', dataIndex: 'sale_date', width: 100 }
              ]}
            />
          </>
        )}
      </Modal>

      {/* 编辑客户弹框 */}
      <Modal title="编辑客户信息" open={editModalVisible} onCancel={() => setEditModalVisible(false)} onOk={handleEditSubmit} confirmLoading={editLoading}>
        <Form form={editForm} layout="vertical">
          <div style={{ display: 'flex', gap: 16 }}>
            <Form.Item name="last_name" label="姓" rules={[{ required: true }]} style={{ flex: 1 }}>
              <Input placeholder="请输入姓" />
            </Form.Item>
            <Form.Item name="first_name" label="名" rules={[{ required: true }]} style={{ flex: 1 }}>
              <Input placeholder="请输入名" />
            </Form.Item>
          </div>
          <div style={{ display: 'flex', gap: 16 }}>
            <Form.Item name="gender" label="性别" style={{ flex: 1 }}>
              <Select placeholder="请选择性别" allowClear>
                <Select.Option value={1}>男</Select.Option>
                <Select.Option value={2}>女</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item name="birthday" label="出生日期" style={{ flex: 1 }}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </div>
          <Form.Item name="phone" label="手机号" rules={[{ required: true }]}>
            <Input placeholder="请输入手机号" />
          </Form.Item>
          <Form.Item name="email" label="邮箱">
            <Input placeholder="请输入邮箱" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Customers;
